import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { SEOHead } from '@/components/SEOHead';
import { NumberBall } from '@/components/NumberBall';
import { useLanguage } from '@/contexts/LanguageContext';
import { Loader2, Sparkles, TrendingUp, Shuffle, Plus, Trash2, X, Info, AlertCircle } from 'lucide-react';

// =====================================================
// API BASE (ALIGNED WITH OTHER PAGES)
// =====================================================
// Smart API URL that works for both LIVE and PREVIEW
import { API } from '@/config/api';

// Performance logging
const perfLog = (label) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[PERF] ${label}: ${new Date().toISOString()}`);
  }
};

export const PredictionsPage = () => {
  const { lang } = useParams();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();

  const [lotteries, setLotteries] = useState([]);
  const [selectedLottery, setSelectedLottery] = useState('');

  // models: statistical | hybrid | lotterics
  const [method, setMethod] = useState('statistical');

  const [combinations, setCombinations] = useState(5);
  const [randomPercent, setRandomPercent] = useState(30); // HYBRID only
  const [extraRandom, setExtraRandom] = useState(0); // outside-model
  const [attractionCode, setAttractionCode] = useState('');
  const [excludeHistorical, setExcludeHistorical] = useState(false);

  // Universe Code state (symbolic layer - frontend only)
  const [universeCode, setUniverseCode] = useState('');

  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLotteries, setLoadingLotteries] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  
  // Guard to prevent duplicate lottery fetch
  const lotteriesFetchedRef = useRef(false);

  // =====================================================
  // UNIVERSE CODE TWIST LOGIC (Client-side only)
  // =====================================================
  
  // Generate a deterministic "Universe Pick" number from the code
  const generateUniversePick = useCallback((code, lottery) => {
    if (!code || !lottery) return null;
    
    // Get max number from lottery config
    const maxNum = lottery?.main_numbers?.max || 50;
    const minNum = lottery?.main_numbers?.min || 1;
    
    // Deterministic seed from code + today's date
    const today = new Date();
    const day = today.getDate();
    const month = today.getMonth() + 1;
    
    let charSum = 0;
    for (let i = 0; i < code.length; i++) {
      charSum += code.charCodeAt(i);
    }
    
    // Generate number: (charSum + day + month) % range + min
    const range = maxNum - minNum + 1;
    const seed = (charSum + day * 7 + month * 31) % range;
    const universeNumber = minNum + seed;
    
    return universeNumber;
  }, []);

  // Load Universe Code from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('lotterics_universe_code');
    if (stored) {
      setUniverseCode(stored);
    }
  }, []);

  // Save Universe Code to localStorage when changed
  const handleUniverseCodeChange = (value) => {
    setUniverseCode(value);
    if (value) {
      localStorage.setItem('lotterics_universe_code', value);
    } else {
      localStorage.removeItem('lotterics_universe_code');
    }
  };

  // Get current lottery config for Universe Pick calculation
  const currentLottery = lotteries.find(l => l.code === selectedLottery);
  const universePick = universeCode ? generateUniversePick(universeCode, currentLottery) : null;

  // =====================================================
  // LOAD LOTTERIES (render-first - immediate)
  // =====================================================

  useEffect(() => {
    if (lotteriesFetchedRef.current) return;
    lotteriesFetchedRef.current = true;
    
    perfLog('shell render');
    
    const loadLotteries = async () => {
      try {
        const res = await api.get(`${API}/lotteries`);
        const data = Array.isArray(res.data) ? res.data : [];
        setLotteries(data);

        const qp = searchParams.get('lottery');
        if (qp) {
          setSelectedLottery(qp);
        } else if (data.length > 0) {
          setSelectedLottery(data[0].code);
        }
        perfLog('overview populated');
      } catch (e) {
        setErrorMsg('Failed to load lotteries. Check backend connectivity.');
        setLotteries([]);
        setSelectedLottery('');
      } finally {
        setLoadingLotteries(false);
      }
    };

    loadLotteries();
    // Don't reset ref in cleanup - it prevents duplicate fetches
  }, [searchParams]);

  // =====================================================
  // GENERATE PREDICTIONS (user-triggered, not auto)
  // =====================================================

  const generatePredictions = useCallback(async (append = false) => {
    if (!selectedLottery) return;

    setLoading(true);
    setErrorMsg('');
    perfLog('predictions fetch started');

    try {
      const payload = {
        lottery_id: selectedLottery,
        method,
        combinations: Math.min(Math.max(combinations, 1), 10),
        attraction_code: attractionCode || null,
        extra_random_percent: Math.max(0, Math.min(extraRandom, 100)),
        exclude_historical_main: excludeHistorical,
      };

      if (method === 'hybrid') {
        payload.random_percent = Math.max(0, Math.min(randomPercent, 50));
      }

      const res = await api.post(`${API}/predictions/generate`, payload);
      const data = res.data || {};
      const rawList = data.combinations || data.predictions || [];
      const list = rawList.map(p => ({
        ...p,
        meta: p.meta || p.metadata || p.score || {},
      }));

      setPredictions(prev => (append ? [...prev, ...list] : list));
      perfLog('predictions populated');
    } catch (e) {
      setErrorMsg('Failed to generate predictions. Check backend response.');
    } finally {
      setLoading(false);
    }
  }, [selectedLottery, method, combinations, attractionCode, extraRandom, excludeHistorical, randomPercent]);

  // =====================================================
  // UI CONFIG
  // =====================================================

  const modelConfig = {
    statistical: { label: 'Statistical', icon: <TrendingUp className="w-4 h-4" /> },
    hybrid: { label: 'Hybrid', icon: <Shuffle className="w-4 h-4" /> },
    lotterics: { label: 'Lotterics', icon: <Sparkles className="w-4 h-4" /> },
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="max-w-7xl mx-auto px-4 py-4">
      <SEOHead
        title={t('predictionsPage.title')}
        description={t('predictionsPage.subtitle')}
        path="/statistical-projections"
      />

      <h1 className="text-3xl font-bold text-white mb-2">{t('predictionsPage.title')}</h1>
      <p className="text-gray-400 text-sm mb-4">
        {t('predictionsPage.subtitle')}
      </p>

      {/* STATIC CLARIFICATION BOX - ADSENSE COMPLIANCE */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-blue-400 font-semibold text-sm mb-1">{t('predictionsPage.aboutTitle')}</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              {t('predictionsPage.aboutP1')}
            </p>
          </div>
        </div>
      </div>

      {errorMsg && (
        <div className="bg-red-900/40 border border-red-700 text-red-200 text-xs p-3 rounded mb-4">
          {errorMsg}
        </div>
      )}

      {/* CONTROLS */}
      <div className="bg-gray-900/40 border border-gray-700 rounded-lg p-4 mb-4 flex flex-wrap gap-4 items-end">
        {/* Lottery */}
        <div className="w-[200px]">
          <Label className="text-xs text-gray-400">{t('predictionsPage.lottery')}</Label>
          <Select value={selectedLottery} onValueChange={setSelectedLottery} disabled={loadingLotteries}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
              <SelectValue placeholder={t('predictionsPage.selectLottery')} />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              {lotteries.map(l => (
                <SelectItem key={l.code} value={l.code} className="text-white">
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Combinations */}
        <div className="w-[120px]">
          <Label className="text-xs text-gray-400">{t('predictionsPage.combinations')}</Label>
          <Input type="number" min={1} max={10} value={combinations} onChange={e => setCombinations(+e.target.value)} />
        </div>

        {/* Model */}
        <div className="w-[160px]">
          <Label className="text-xs text-gray-400">{t('predictionsPage.model')}</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              {Object.entries(modelConfig).map(([k, v]) => (
                <SelectItem key={k} value={k} className="text-white">
                  <div className="flex items-center gap-2">{v.icon}{v.label}</div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Hybrid Random % */}
        {method === 'hybrid' && (
          <div className="w-[120px]">
            <Label className="text-xs text-gray-400">{t('predictionsPage.randomHybrid')}</Label>
            <Input type="number" min={0} max={50} value={randomPercent} onChange={e => setRandomPercent(+e.target.value)} />
          </div>
        )}

        {/* Extra Random */}
        <div className="w-[140px]">
          <Label className="text-xs text-gray-400">{t('predictionsPage.extraRandom')}</Label>
          <Input type="number" min={0} max={100} value={extraRandom} onChange={e => setExtraRandom(+e.target.value)} />
        </div>

        {/* Attraction */}
        <div className="w-[160px]">
          <Label className="text-xs text-gray-400">{t('predictionsPage.attractionCode')}</Label>
          <Input value={attractionCode} onChange={e => setAttractionCode(e.target.value)} />
        </div>

        {/* Universe Code (optional symbolic) */}
        <div className="w-[180px]">
          <Label className="text-xs text-indigo-400 flex items-center gap-1">
            🌌 Universe Code
            <span className="text-gray-500">(optional)</span>
          </Label>
          <Input 
            value={universeCode} 
            onChange={e => handleUniverseCodeChange(e.target.value.toUpperCase())} 
            placeholder="e.g. UNIV-7F3K9"
            className="border-indigo-500/30 focus:border-indigo-500"
          />
        </div>

        {/* Exclude historical */}
        <div className="flex items-center gap-2">
          <Checkbox checked={excludeHistorical} onCheckedChange={setExcludeHistorical} />
          <Label className="text-xs text-gray-400">{t('predictionsPage.excludeHistorical')}</Label>
        </div>

        {/* V5 Engine Banner - Powerball USA & La Primitiva + Lotterics only */}
        {["powerball", "laprimitiva"].includes(selectedLottery) && method === "lotterics" && (
          <div className="text-xs text-amber-400/80 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1">
            ✨ <strong>{t('predictionsPage.v5Engine')}</strong>
          </div>
        )}

        {/* Buttons */}
        <Button onClick={() => generatePredictions(false)} disabled={loading || !selectedLottery} className="bg-amber-500 text-white">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}{t('predictionsPage.generate')}
        </Button>

        <Button variant="outline" disabled={predictions.length === 0 || loading} onClick={() => generatePredictions(true)}>
          <Plus className="w-4 h-4 mr-1" /> {t('predictionsPage.addMore')}
        </Button>

        <Button variant="outline" className="text-red-400 border-red-700" onClick={() => setPredictions([])}>
          <Trash2 className="w-4 h-4 mr-1" /> {t('predictionsPage.clear')}
        </Button>
      </div>

      {/* Universe Code Info (shown when code is entered) */}
      {universeCode && (
        <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 text-indigo-400 text-sm">
            <span>🌌</span>
            <span className="font-medium">{t('predictionsPage.universeCodeActive')}:</span>
            <code className="bg-gray-800 px-2 py-0.5 rounded text-white">{universeCode}</code>
            {universePick && (
              <span className="text-gray-400 ml-2">
                → {t('predictionsPage.todaysUniversePick')}: <strong className="text-indigo-300">{universePick}</strong>
              </span>
            )}
          </div>
          <p className="text-gray-500 text-xs mt-1">
            {t('predictionsPage.universePickNote')}
          </p>
        </div>
      )}

      {/* RESULTS */}
      {predictions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {predictions.map((p, i) => (
            <div key={i} className="bg-gray-900 border border-gray-700 rounded-lg p-4 relative">
              <button className="absolute top-2 right-2 text-gray-500 hover:text-red-400" onClick={() => setPredictions(prev => prev.filter((_, idx) => idx !== i))}>
                <X className="w-4 h-4" />
              </button>

              <div className="text-xs text-gray-400 mb-2">{t('predictionsPage.prediction')} #{i + 1}</div>
              <div className="flex flex-wrap gap-2 mb-3">
                {p.main_numbers?.map((n, idx) => <NumberBall key={idx} number={n} variant="main" size="sm" />)}
                {p.bonus_numbers?.length > 0 && (
                  <>
                    <span className="text-gray-500">+</span>
                    {p.bonus_numbers.map((n, idx) => <NumberBall key={idx} number={n} variant="bonus" size="sm" />)}
                  </>
                )}
                
                {/* Reintegro for La Primitiva */}
                {p.reintegro !== null && p.reintegro !== undefined && (
                  <>
                    <span className="text-gray-500">+</span>
                    <div className="relative">
                      <NumberBall number={p.reintegro} variant="bonus" size="sm" />
                      <span className="absolute -top-1 -right-1 text-[8px] bg-blue-400 text-black px-1 rounded">R</span>
                    </div>
                  </>
                )}
                
                {/* Universe Pick - shown only when Universe Code is active */}
                {universeCode && universePick && (
                  <>
                    <span className="text-indigo-400 mx-1">🌌</span>
                    <div className="relative">
                      <NumberBall number={universePick} variant="bonus" size="sm" />
                      <span className="absolute -top-1 -right-1 text-[8px] bg-indigo-500 text-white px-1 rounded">U</span>
                    </div>
                  </>
                )}
              </div>
              <div className="text-[11px] text-gray-400">
                {t('predictionsPage.sum')}: <span className="text-white">{p.meta?.sum}</span> · {t('predictionsPage.pattern')}: <span className="text-white">{p.meta?.even_odd}</span>
              </div>
              
              {/* Universe Pick label */}
              {universeCode && universePick && (
                <div className="text-[10px] text-indigo-400/70 mt-2 border-t border-gray-800 pt-2">
                  🌌 {t('predictionsPage.universePickLabel')}: {universePick} ({t('predictionsPage.universePickFromCode')})
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && predictions.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          {t('predictionsPage.selectAndGenerate')}
        </div>
      )}

      {/* Link to Prayer page */}
      <div className="mt-6 text-center">
        <Link 
          to={`/${lang || 'en'}/prayer-to-universe`}
          className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 text-sm transition-colors"
        >
          <Sparkles className="w-4 h-4" />
          {t('predictionsPage.getUniverseCode')}
        </Link>
      </div>

      <div className="mt-10 text-xs text-gray-500">
        {t('predictionsPage.disclaimer')}
      </div>
    </div>
  );
};

export default PredictionsPage;
